/**
 * Connection Status Service
 * Detects online/offline state and notifies subscribers
 */

class ConnectionStatus {
  constructor() {
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.listeners = new Set();
    this._boundOnline = () => this._setOnline(true);
    this._boundOffline = () => this._setOnline(false);

    if (typeof window !== 'undefined') {
      this._init();
    }
  }

  _init() {
    window.addEventListener('online', this._boundOnline);
    window.addEventListener('offline', this._boundOffline);
  }

  _setOnline(status) {
    if (this.isOnline !== status) {
      this.isOnline = status;
      this._notify();
    }
  }

  _notify() {
    this.listeners.forEach(cb => {
      try {
        cb(this.isOnline);
      } catch (e) {
        // silent
      }
    });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('connection-change', {
        detail: { online: this.isOnline }
      }));
    }
  }

  /**
   * Subscribe to connection changes
   * @param {Function} callback - Called with boolean (online status)
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);
    // Immediately call with current state
    callback(this.isOnline);
    return () => this.listeners.delete(callback);
  }

  /**
   * Check real connectivity by fetching a resource
   * @returns {Promise<boolean>}
   */
  async checkConnectivity() {
    if (!navigator.onLine) return false;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/favicon.png', {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Cleanup listeners
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this._boundOnline);
      window.removeEventListener('offline', this._boundOffline);
    }
    this.listeners.clear();
  }
}

export const connectionStatus = new ConnectionStatus();
